import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Download, Loader2, AlertTriangle } from "lucide-react";
import api, { API, openDownload } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const lineAmount = (li) =>
  (li.kind || "item") !== "item" || li.rate_only
    ? 0
    : (Number(li.quantity) || 0) * (Number(li.unit_rate) || 0);

export default function QuotationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoOk, setLogoOk] = useState(true);

  useEffect(() => {
    api.get(`/rfqs/${id}`)
      .then((r) => setRfq(r.data))
      .catch(() => navigate(`/rfq/${id}`))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !rfq) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const cur = user?.currency || rfq.currency || "$";
  const lines = rfq.line_items || [];
  const items = lines.filter((l) => (l.kind || "item") === "item");
  const sub = items.reduce((s, l) => s + lineAmount(l), 0);
  const discount = rfq.discount_type === "amount"
    ? Number(rfq.discount_value || 0)
    : sub * Number(rfq.discount_value || 0) / 100;
  const net = sub - discount;
  const taxPct = Number(rfq.tax_percent || 0);
  const tax = net * taxPct / 100;
  const total = net + tax;

  // group lines by sheet/tab, preserving order
  const sheetOrder = [];
  lines.forEach((l) => { const s = l.sheet || ""; if (!sheetOrder.includes(s)) sheetOrder.push(s); });
  const groups = sheetOrder.map((s) => ({ name: s, lines: lines.filter((l) => (l.sheet || "") === s) }));

  const intro = rfq.intro || user?.quote_intro || "";
  const bank = rfq.bank_details || user?.bank_details || "";
  const terms = rfq.terms || user?.terms || "";

  const warnings = [];
  if (!rfq.buyer_name) warnings.push("Client / contractor name is missing.");
  if (items.length === 0) warnings.push("This quotation has no line items.");
  const unpriced = items.filter((l) => !l.rate_only && !(Number(l.unit_rate) > 0)).length;
  if (unpriced > 0) warnings.push(`${unpriced} line item(s) have no rate yet.`);
  if (!user?.company_name) warnings.push("Your company name is missing — set it in Vendor Profile.");
  if (!user?.address) warnings.push("Your company address is missing — set it in Vendor Profile.");
  if (!logoOk || !user?.has_logo) warnings.push("No company logo — add one in Vendor Profile for a branded quotation.");

  const dateStr = shortDate(rfq.sent_at || rfq.created_at || new Date().toISOString());

  let rowNo = 0;

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:bg-white print:py-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .q-page { box-shadow: none !important; margin: 0 !important; width: 100% !important; }
          @page { size: A4; margin: 14mm; }
        }
      `}</style>

      {/* toolbar */}
      <div className="no-print max-w-[820px] mx-auto mb-4 flex items-center justify-between px-4">
        <button data-testid="quotation-back" onClick={() => navigate(`/rfq/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to Quote Builder
        </button>
        <div className="flex items-center gap-2">
          <Button data-testid="quotation-print" onClick={() => window.print()} className="gap-2 h-10 font-semibold">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </Button>
          <Button data-testid="quotation-download-pdf" variant="outline" onClick={() => openDownload(`/rfqs/${id}/pdf`)} className="gap-2 h-10 font-semibold">
            <Download className="w-4 h-4" /> Download PDF
          </Button>
        </div>
      </div>

      {warnings.length > 0 && (
        <div data-testid="quotation-warnings" className="no-print max-w-[820px] mx-auto mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2 font-semibold text-amber-800 mb-1.5">
            <AlertTriangle className="w-4 h-4" /> Please review before sending
          </div>
          <ul className="list-disc pl-6 text-sm text-amber-800 space-y-0.5">
            {warnings.map((w, i) => <li key={i} data-testid={`quotation-warning-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* A4 quotation */}
      <div className="q-page max-w-[820px] mx-auto bg-white shadow-xl p-10 text-slate-800" data-testid="quotation-document">
        {/* header */}
        <div className="flex items-start justify-between gap-6 pb-5 border-b-2 border-slate-800">
          <div className="flex items-start gap-4">
            {user?.has_logo && logoOk && (
              <img
                src={`${API}/public/vendors/${user.id}/logo`}
                alt="logo"
                onError={() => setLogoOk(false)}
                className="h-16 w-auto max-w-[180px] object-contain"
              />
            )}
            <div>
              <div className="text-lg font-bold text-slate-900">{user?.company_name || "Your Company"}</div>
              {user?.address && <div className="text-xs text-slate-600 whitespace-pre-line max-w-[280px] mt-0.5">{user.address}</div>}
              <div className="text-xs text-slate-600 mt-0.5">
                {user?.phone && <span>{user.phone}</span>}
                {user?.phone && user?.contact_email && <span> · </span>}
                {user?.contact_email && <span>{user.contact_email}</span>}
              </div>
              {user?.vat_number && <div className="text-xs text-slate-600 mt-0.5">TRN / VAT: {user.vat_number}</div>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl font-extrabold tracking-tight text-slate-900">QUOTATION</div>
            <div className="text-xs text-slate-600 mt-1">Ref: <span className="font-semibold">{rfq.quote_ref_display || rfq.quote_ref}</span></div>
            <div className="text-xs text-slate-600">Date: {dateStr}</div>
            <div className="text-xs text-slate-600">Valid for: {rfq.validity_days} days</div>
          </div>
        </div>

        {/* buyer */}
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Quotation To</div>
          <div className="font-semibold text-slate-900 mt-0.5">{rfq.buyer_name || "—"}</div>
          {rfq.buyer_address && <div className="text-xs text-slate-600 whitespace-pre-line">{rfq.buyer_address}</div>}
          {rfq.name && <div className="text-xs text-slate-600 mt-1">Project: {rfq.name}</div>}
        </div>

        {/* intro */}
        {intro && <div className="mt-5 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{intro}</div>}

        {/* items */}
        <table className="w-full mt-6 text-sm border-collapse">
          <thead>
            <tr className="bg-slate-800 text-white text-xs">
              <th className="text-left py-2 px-2 w-10">#</th>
              <th className="text-left py-2 px-2">Description</th>
              <th className="text-right py-2 px-2 w-16">Qty</th>
              <th className="text-left py-2 px-2 w-16">Unit</th>
              <th className="text-right py-2 px-2 w-24">Rate</th>
              <th className="text-right py-2 px-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <React.Fragment key={gi}>
                {g.name && groups.length > 1 && (
                  <tr className="bg-slate-100">
                    <td colSpan={6} className="py-1.5 px-2 font-bold text-slate-800 text-xs uppercase tracking-wide">{g.name}</td>
                  </tr>
                )}
                {g.lines.map((li, i) => {
                  const isItem = (li.kind || "item") === "item";
                  if (!isItem) {
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td></td>
                        <td colSpan={5} className="py-1.5 px-2 font-semibold text-slate-700">{li.boq_description}</td>
                      </tr>
                    );
                  }
                  rowNo += 1;
                  return (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="py-1.5 px-2 text-slate-400">{rowNo}</td>
                      <td className="py-1.5 px-2">{li.matched_description || li.boq_description}{li.rate_note ? <span className="text-xs text-slate-400"> ({li.rate_note})</span> : null}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{li.rate_only ? "" : Number(li.quantity || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2">{li.unit}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{li.rate_only ? "" : money(li.unit_rate, cur)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{li.rate_only ? "" : money(lineAmount(li), cur)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* totals */}
        <div className="flex justify-end mt-5">
          <div className="w-72 text-sm">
            <div className="flex justify-between py-1"><span className="text-slate-600">Subtotal</span><span className="font-mono">{money(sub, cur)}</span></div>
            {discount > 0 && <div className="flex justify-between py-1"><span className="text-slate-600">Discount</span><span className="font-mono">-{money(discount, cur)}</span></div>}
            {taxPct > 0 && <div className="flex justify-between py-1"><span className="text-slate-600">Tax ({taxPct}%)</span><span className="font-mono">{money(tax, cur)}</span></div>}
            <div className="flex justify-between py-2 mt-1 border-t-2 border-slate-800 font-bold text-base"><span>Grand Total</span><span className="font-mono" data-testid="quotation-grand-total">{money(total, cur)}</span></div>
          </div>
        </div>

        {/* payment terms / bank */}
        {(rfq.payment_terms || bank) && (
          <div className="mt-6 grid grid-cols-2 gap-6 text-xs">
            {rfq.payment_terms && (
              <div>
                <div className="font-semibold text-slate-800 mb-1">Payment Terms</div>
                <div className="text-slate-600 whitespace-pre-line">{rfq.payment_terms}</div>
              </div>
            )}
            {bank && (
              <div>
                <div className="font-semibold text-slate-800 mb-1">Bank Details</div>
                <div className="text-slate-600 whitespace-pre-line">{bank}</div>
              </div>
            )}
          </div>
        )}

        {/* terms */}
        {terms && (
          <div className="mt-6 text-xs">
            <div className="font-semibold text-slate-800 mb-1">Terms &amp; Conditions</div>
            <div className="text-slate-600 whitespace-pre-line leading-relaxed">{terms}</div>
          </div>
        )}

        {/* system-generated note */}
        <div className="mt-10 pt-6 border-t border-slate-200 text-center" data-testid="quotation-system-note">
          <p className="text-xs text-slate-500 italic">This is a system-generated quotation and does not require a signature.</p>
        </div>
      </div>
    </div>
  );
}
